import { SearchWidgetTheme } from "@xapp/intelligent-search-widget";
import React, { Dispatch, FC, SetStateAction } from "react";
import { useOnChangeChildDispatch } from "utils/OnChange";
import { WidgetColorPicker } from "../WidgetThemeBuilder/WidgetColorPicker";
import { SearchWidgetBorderThemeBuilder } from "./SearchWidgetBorderThemeBuilder";
import { SearchWidgetBubblesThemeBuilder } from "./SearchWidgetBubblesThemeBuilder";
import { SearchWidgetCardThemeBuilder } from "./SearchWidgetCardThemeBuilder";
import { SearchWidgetCarouselThemeBuilder } from "./SearchWidgetCarouselThemeBuilder";
import { SearchWidgetListThemeBuilder } from "./SearchWidgetListThemeBuilder";
import { IntelligentSearchWidgetCardTheme, IntelligentSearchWidgetTheme, IntelligentSearchWidgetBorderStyle } from "generated/graphql";

export interface SearchWidgetThemeBuilderProps {
    readonly theme: SearchWidgetTheme;
    readonly defaultTheme: SearchWidgetTheme;
    readonly onChange: Dispatch<SetStateAction<SearchWidgetTheme>>;
}

const EMPTY_THEME: SearchWidgetTheme = {};

export const SearchWidgetThemeBuilder: FC<SearchWidgetThemeBuilderProps> = props => {
    const theme: SearchWidgetTheme = props.theme ?? EMPTY_THEME;
    const defaultTheme: SearchWidgetTheme = props.defaultTheme ?? EMPTY_THEME;

    const { onChange } = props;

    const handlePrimaryColorChange = useOnChangeChildDispatch("accentColor", onChange);
    const handleBorderChange = useOnChangeChildDispatch("border", onChange);
    const handleMessagesChange = useOnChangeChildDispatch("messages", onChange);
    const handleCardChange = useOnChangeChildDispatch("card", onChange);
    const handleCarouselChange = useOnChangeChildDispatch("carousel", onChange);
    const handleListChange = useOnChangeChildDispatch("list", onChange);

    return <>
        <form>
            <WidgetColorPicker key="primaryColor" label="Primary Color" value={theme.accentColor} defaultValue="#0000cc" onChange={handlePrimaryColorChange} />
            <SearchWidgetBorderThemeBuilder key="border" theme={theme.border} defaultTheme={defaultTheme.border} onChange={handleBorderChange} />
            <SearchWidgetBubblesThemeBuilder key="bubbles" theme={theme.messages} defaultTheme={defaultTheme.messages} onChange={handleMessagesChange} />
            <SearchWidgetCardThemeBuilder key="card" theme={theme.card} defaultTheme={defaultTheme.card} onChange={handleCardChange} />
            <SearchWidgetCarouselThemeBuilder key="carousel" theme={theme.carousel} defaultTheme={defaultTheme.carousel} onChange={handleCarouselChange} />
            <SearchWidgetListThemeBuilder key="list" theme={theme.list} defaultTheme={defaultTheme.list} onChange={handleListChange} />
        </form>
    </>;
};


export function buildEmptyTheme(accentColor: string): SearchWidgetTheme {
    const fontFamily = "RobotoRegular";

    const card: SearchWidgetTheme["card"] = {
        margin: {
            top: "0px",
            right: "0px",
            bottom: "20px",
            left: "0px",
        },
        padding: {
            top: "0px",
            right: "0px",
            bottom: "0px",
            left: "0px",
        },
        border: {
            width: "1px",
            color: accentColor,
            style: "solid",
            radius: "12px"
        },
        background: "#ffffff",
        title: {
            text: {
                color: "#000000",
                fontSize: "24px",
                fontFamily,
                fontWeight: "normal"
            },
            margin: {
                top: "0px",
                right: "0px",
                bottom: "0px",
                left: "0px",
            },
            padding: {
                top: "0px",
                right: "0px",
                bottom: "0px",
                left: "0px",
            }
        },
        description: {
            text: {
                color: "#000000",
                fontSize: "16px",
                fontFamily,
                fontWeight: "normal"
            },
            margin: {
                top: "16px",
                right: "0px",
                bottom: "0px",
                left: "0px",
            },
            padding: {
                top: "0px",
                right: "0px",
                bottom: "0px",
                left: "0px",
            }
        },
        link: {
            text: {
                default: {
                    color: "#0000ff",
                    fontSize: "16px",
                    fontFamily,
                    fontWeight: "normal"
                }
            },
            margin: {
                top: "0px",
                right: "0px",
                bottom: "0px",
                left: "0px",
            },
            padding: {
                top: "0px",
                right: "0px",
                bottom: "0px",
                left: "0px",
            }
        }
    };

    return {
        accentColor,
        border: {
            width: "1px",
            color: accentColor,
            style: "solid",
            radius: "17.5px"
        },
        card,
        carousel: {
            card
        },
        list: {
            card
        },
        messages: {
            others: {
                bubble: {
                    background: "#ffffff"
                },
                text: {
                    color: "#000000",
                    fontSize: "16px",
                    fontFamily,
                    fontWeight: "normal"
                }
            }
        }
    };
}

/**
 *
 * @param accentColor
 * @returns
 */
export function buildDefaultSearchTheme(accentColor: string): IntelligentSearchWidgetTheme {

    const fontFamily = "RobotoRegular";
    //IntelligentSearchWidgetCardTheme
    const card: IntelligentSearchWidgetCardTheme   /*SearchWidgetTheme["card"] */ = {
        margin: {
            top: "0px",
            right: "0px",
            bottom: "20px",
            left: "0px",
        },
        padding: {
            top: "0px",
            right: "0px",
            bottom: "0px",
            left: "0px",
        },
        border: {
            width: "1px",
            color: accentColor,
            style: IntelligentSearchWidgetBorderStyle.Solid,  // "solid",
            radius: "12px"
        },
        background: "#ffffff",
        title: {
            text: {
                color: "#000000",
                fontSize: "24px",
                fontFamily,
                fontWeight: "normal"
            },
            margin: {
                top: "0px",
                right: "0px",
                bottom: "0px",
                left: "0px",
            },
            padding: {
                top: "0px",
                right: "0px",
                bottom: "0px",
                left: "0px",
            }
        },
        description: {
            text: {
                color: "#000000",
                fontSize: "16px",
                fontFamily,
                fontWeight: "normal"
            },
            margin: {
                top: "16px",
                right: "0px",
                bottom: "0px",
                left: "0px",
            },
            padding: {
                top: "0px",
                right: "0px",
                bottom: "0px",
                left: "0px",
            }
        },
        link: {
            text: {
                default: {
                    color: "#0000ff",
                    fontSize: "16px",
                    fontFamily,
                    fontWeight: "normal"
                }
            },
            margin: {
                top: "0px",
                right: "0px",
                bottom: "0px",
                left: "0px",
            },
            padding: {
                top: "0px",
                right: "0px",
                bottom: "0px",
                left: "0px",
            }
        }
    };

    return {
        accentColor,
        border: {
            width: "1px",
            color: accentColor,
            style: IntelligentSearchWidgetBorderStyle.Solid, // "solid",
            radius: "17.5px"
        },
        card,
        carousel: {
            card
        },
        list: {
            card
        },
        messages: {
            others: {
                bubble: {
                    background: "#ffffff"
                },
                text: {
                    color: "#000000",
                    fontSize: "16px",
                    fontFamily,
                    fontWeight: "normal"
                }
            }
        }
    };
}
